import { CloseOutlined, CloudUploadOutlined, DeleteOutline } from '@mui/icons-material'
import { Box, CircularProgress, IconButton, LinearProgress, Typography, styled, useTheme } from '@mui/material'
import axios from 'axios'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { endpoints } from 'src/api/file'
import { requestProvider } from 'src/api/requestProvider'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { GridActionButton } from '../common/GridActionButton'
import { DynamicTypography } from '../DynamicTextTypography'
import { FileUrlDocumentLoader } from '../common/input/CommonFileUploader'

const ALLOWED_EXTENSIONS = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx'
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpg',
  'image/jpeg',
  'image/png',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

const getFileNameFromUrl = (url: string) => url.split('/').pop()?.split('_').slice(1).join('_') || url

interface IFileUploadFieldProps {
  value?: string
  disabled?: boolean
  onChange: (url: string) => void
}

export const FileUploadField: React.FC<IFileUploadFieldProps> = ({ value, disabled, onChange }) => {
  const { t } = useTranslation('common')
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [uploadingName, setUploadingName] = useState('')

  const handleFile = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0]
    if (!file) return

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(t('file-type-not-allowed', { ns: 'overall-reports' }))
      evt.target.value = ''
      return
    }

    const controller = new AbortController()
    abortControllerRef.current = controller
    setUploadingName(file.name)
    setProgress(0)
    setUploading(true)

    const form = new FormData()
    form.append('file', file)

    try {
      const result = await requestProvider.post<any>(endpoints.uploadFile(), form, {
        signal: controller.signal,
        onUploadProgress: (progressEvent: ProgressEvent) => {
          if (progressEvent.total) {
            setProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100))
          }
        },
      })
      onChange(result.data.url)
    } catch (err) {
      if (!axios.isCancel(err)) {
        toast.error(t('upload-failed'))
      }
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const removeFile = () => {
    if (inputRef.current) inputRef.current.value = ''
    onChange('')
  }

  return (
    <Box
      border={`1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`}
      borderRadius={'12px'}
      p={'12px 14px'}
      maxWidth={'500px'}
      bgcolor={getThemeColor(theme, MUIThemeColors.white)}
    >
      <input ref={inputRef} type="file" hidden disabled={disabled} accept={ALLOWED_EXTENSIONS} onChange={handleFile} />
      {uploading ? (
        <Box display="flex" alignItems="center" gap={'14px'}>
          <Box
            width={44}
            height={44}
            borderRadius={'10px'}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            bgcolor={getThemeColor(theme, MUIThemeColors.secondaryLight)}
          >
            <CircularProgress size={22} thickness={4.5} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Box display="flex" justifyContent="space-between" gap={'10px'}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {uploadingName}
              </Typography>
              <Typography variant="caption" color="text.secondary" flexShrink={0}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ mt: 1, borderRadius: 99, height: 6 }} />
          </Box>
          <IconButton size="small" aria-label={t('cancel')} onClick={() => abortControllerRef.current?.abort()}>
            <CloseOutlined fontSize="small" />
          </IconButton>
        </Box>
      ) : value ? (
        <Box display="flex" alignItems="center" gap={'14px'}>
          <FileUrlDocumentLoader contentTypeValue="application/octet-stream" fileUrl={value} />
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {getFileNameFromUrl(value)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('file-uploaded')}
            </Typography>
          </Box>
          {!disabled && (
            <IconButton size="small" aria-label={t('delete')} onClick={removeFile}>
              <DeleteOutline fontSize="small" color="error" />
            </IconButton>
          )}
        </Box>
      ) : (
        <Box display="flex" alignItems="center" gap={'14px'} flexWrap="wrap">
          <Box
            width={44}
            height={44}
            borderRadius={'10px'}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            bgcolor={getThemeColor(theme, MUIThemeColors.primaryMain)}
            color={getThemeColor(theme, MUIThemeColors.white)}
          >
            <CloudUploadOutlined sx={{ color: 'white' }} />
          </Box>
          <Box flex={'1 1 100px'} minWidth={0}>
            <DynamicTypography text={t('uploadFile')} variant="body2" fontWeight={600} />
            <DynamicTypography text={ALLOWED_EXTENSIONS} variant="caption" color="text.secondary" />
          </Box>
          <StyledChooseFileButton
            variant="outlined"
            buttonText={t('choose-file')}
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          />
        </Box>
      )}
    </Box>
  )
}

export default FileUploadField

const StyledChooseFileButton = styled(GridActionButton)({
  flexShrink: 0,
  marginLeft: 'auto',
  flexBasis: 'calc((300px - 100%) * 999)',
  maxWidth: '100%',
})
